"use client";

import { DatePicker } from "@/components/ui/date-picker";
import PageHeader from "@/components/layout/PageHeader";
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Eye,
  Trash2,
  Target,
  Building,
  Phone,
  Mail,
  MapPin,
  Calendar,
  User,
  DollarSign,
  X,
  AlertCircle,
  Save,
  Clock,
  Edit3,
  PhoneCall,
  Send,
  FileText,
  CalendarCheck,
  Users,
  Star,
  ChevronLeft,
  ChevronRight,
  Building2,
} from "lucide-react";
import { FaProjectDiagram } from "react-icons/fa";
import { ColumnDef } from "@tanstack/react-table";
import {
  TableProvider,
  DataTableHeader,
  DataTableHeaderGroup,
  DataTableHead,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  TableActions,
  TableDropdown,
  TableDropdownButton,
  TableDropdownMenu,
  TableDropdownItem,
  useTableDropdown,
} from "../../components/ui/data-table";
import { get, put, del, post } from "../../utils/service";
import Drawer from "@/components/ui/Drawer";

interface Lead {
  id: string;
  company_name: string;
  contact_person: string;
  email: string;
  phone?: string;
  source: string;
  priority: string;
  type: string;
  industry: string;
  location: string;
  current_stage: number;
  status: string;
  created_at: string;
  created_by: string;
  first_name: string;
  last_name: string;
  notes?: string;
  decision_makers?: string;
  budget_range?: string;
  currency: string;
  value?: string | number;
  last_contacted_at?: string;
  last_contacted_by?: string;
  contacted_by_first_name?: string;
  contacted_by_last_name?: string;
  lead_source?: string;
  affiliate_potential?: string;
  next_action?: string;
  next_action_date?: string;
  next_action_time?: string;
  meeting_title?: string;
  meeting_description?: string;
  meeting_time_start?: string;
  meeting_time_end?: string;
}

interface LeadsResponse {
  success: boolean;
  message: string;
  data: Lead[];
  pagination: {
    total: number;
    current_page: number;
    per_page: number;
    total_pages: number;
  };
}

interface PaginationState {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

const STAGES = [
  { id: 1, name: "New Lead", color: "bg-gray-100 text-gray-800" },
  { id: 2, name: "Contacted", color: "bg-blue-100 text-blue-800" },
  { id: 3, name: "Qualified", color: "bg-green-100 text-green-800" },
  { id: 4, name: "Proposal", color: "bg-yellow-100 text-yellow-800" },
  { id: 5, name: "Negotiation", color: "bg-orange-100 text-orange-800" },
  { id: 6, name: "Closed Won", color: "bg-purple-100 text-purple-800" },
];

const PRIORITY_LEVELS = ["low", "medium", "high"];
const LEAD_SOURCES = [
  "website",
  "referral",
  "social_media",
  "cold_call",
  "event",
  "linkedin",
  "google_ads",
  "email_campaign",
  "trade_show",
  "partner",
  "other",
];
const AFFILIATE_POTENTIALS = ["none", "low", "medium", "high", "strategic_partner"];
const NEXT_ACTIONS = [
  { id: "call", name: "Call", icon: "phone" },
  { id: "email", name: "Email", icon: "mail" },
  { id: "send_brochure", name: "Send Brochure", icon: "file" },
  { id: "book_meeting", name: "Book Meeting", icon: "calendar" },
];
const PROJECT_TYPES = [
  "app_development",
  "web_development",
  "mobile_app",
  "web_design",
  "ui_ux_design",
  "figma_design",
  "talent_placement",
  "software_consulting",
  "api_development",
  "database_design",
  "cloud_infrastructure",
  "devops",
  "qa_testing",
  "maintenance_support",
  "custom_software",
];
const INDUSTRIES = [
  "technology",
  "healthcare",
  "finance",
  "retail",
  "education",
  "manufacturing",
  "hospitality",
  "real_estate",
  "transportation",
  "media_entertainment",
  "nonprofit",
  "government",
  "agriculture",
  "energy",
  "telecommunications",
  "other",
];
const BUDGET_RANGES = [
  "0-100",
  "100-500",
  "500-1000",
  "1000-2500",
  "2500-5000",
  "5000-10000",
  "10000-25000",
  "25000-50000",
  "50000-100000",
  "100000-250000",
  "250000-500000",
  "500000-1000000",
  "1000000+",
];

const CUSTOM_BUDGET = "__custom__";

const SkeletonRow = () => (
  <div className="animate-pulse p-6">
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded"></div>
  </div>
);

const FloatingDrawer = ({
  isOpen,
  onClose,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };
    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  const drawerPosition = isMobile || isTablet ? "bottom" : "right";

  if (drawerPosition === "bottom") {
    return (
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        position="bottom"
        heightPercentage={80}
        showCloseButton={false}
      >
        <div className="h-full flex flex-col bg-black text-white">
          {children}
        </div>
      </Drawer>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 z-[99998]"
            onClick={onClose}
          />

          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{
              type: "spring",
              damping: 30,
              stiffness: 300,
            }}
            className="fixed top-4 right-4 bottom-4 w-96 bg-black border border-gray-800 shadow-2xl rounded-2xl z-[99999] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col bg-black text-white">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Inline Date Picker Component (renders within parent drawer)
const InlineDatePicker = ({
  isOpen,
  onClose,
  value,
  onChange,
  title = "Select Date",
}: {
  isOpen: boolean;
  onClose: () => void;
  value: string;
  onChange: (date: string) => void;
  title?: string;
}) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (value) {
      const d = new Date(value);
      return new Date(d.getFullYear(), d.getMonth(), 1);
    }
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  });

  const [selectedDate, setSelectedDate] = useState(value);

  useEffect(() => {
    if (isOpen && value) {
      setSelectedDate(value);
      const d = new Date(value);
      setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  }, [isOpen, value]);

  const daysInMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  ).getDate();

  const firstDayOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  ).getDay();

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const selectDate = (day: number) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    setSelectedDate(dateStr);
  };

  const handleConfirm = () => {
    onChange(selectedDate);
  };

  const isSelected = (day: number) => {
    if (!selectedDate) return false;
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return dateStr === selectedDate;
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentMonth.getMonth() === today.getMonth() &&
      currentMonth.getFullYear() === today.getFullYear()
    );
  };

  const isPastDate = (day: number) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return checkDate < today;
  };

  const canGoToPrevMonth = () => {
    const today = new Date();
    const firstOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const firstOfDisplayMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    return firstOfDisplayMonth > firstOfCurrentMonth;
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.2 }}
      className="absolute inset-0 bg-black z-10 overflow-y-auto"
    >
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-white">{title}</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={prevMonth}
            disabled={!canGoToPrevMonth()}
            className={`p-1.5 rounded-lg transition-colors ${
              canGoToPrevMonth()
                ? "bg-gray-800 text-white hover:bg-gray-700"
                : "bg-gray-900 text-gray-600 cursor-not-allowed"
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <h4 className="text-white text-sm font-semibold">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h4>
          <button
            onClick={nextMonth}
            className="p-1.5 rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Day Names */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {dayNames.map((day) => (
            <div key={day} className="text-center text-[10px] text-gray-500 py-1">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1 mb-4">
          {Array.from({ length: firstDayOfMonth }).map((_, i) => (
            <div key={`empty-${i}`} className="aspect-square" />
          ))}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const past = isPastDate(day);
            return (
              <button
                key={day}
                onClick={() => !past && selectDate(day)}
                disabled={past}
                className={`aspect-square rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                  past
                    ? "text-gray-600 cursor-not-allowed"
                    : isSelected(day)
                    ? "bg-primary text-white"
                    : isToday(day)
                    ? "bg-gray-700 text-white"
                    : "text-gray-300 hover:bg-gray-800"
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>

        {/* Selected Date Display */}
        {selectedDate && (
          <div className="bg-gray-900 rounded-lg p-2 mb-4 text-center">
            <p className="text-[10px] text-gray-400">Selected</p>
            <p className="text-white text-xs font-semibold">
              {new Date(selectedDate).toLocaleDateString("en-US", {
                weekday: "short",
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-3 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedDate}
            className="flex-1 px-3 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Inline Time Picker Component (renders within parent drawer)
const InlineTimePicker = ({
  isOpen,
  onClose,
  value,
  onChange,
  title = "Select Time",
  selectedDate = "",
}: {
  isOpen: boolean;
  onClose: () => void;
  value: string;
  onChange: (time: string) => void;
  title?: string;
  selectedDate?: string;
}) => {
  const [selectedHour, setSelectedHour] = useState(() => {
    if (value) {
      const [h] = value.split(":");
      return parseInt(h) || 9;
    }
    return 9;
  });

  const [selectedMinute, setSelectedMinute] = useState(() => {
    if (value) {
      const [, m] = value.split(":");
      return parseInt(m) || 0;
    }
    return 0;
  });

  const [isPM, setIsPM] = useState(() => {
    if (value) {
      const [h] = value.split(":");
      return parseInt(h) >= 12;
    }
    return false;
  });

  useEffect(() => {
    if (isOpen && value) {
      const [h, m] = value.split(":");
      const hour = parseInt(h) || 9;
      setSelectedHour(hour > 12 ? hour - 12 : hour === 0 ? 12 : hour);
      setSelectedMinute(parseInt(m) || 0);
      setIsPM(hour >= 12);
    }
  }, [isOpen, value]);

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  // Check if selected date is today
  const isToday = () => {
    if (!selectedDate) return false;
    const today = new Date();
    const selected = new Date(selectedDate);
    return (
      today.getFullYear() === selected.getFullYear() &&
      today.getMonth() === selected.getMonth() &&
      today.getDate() === selected.getDate()
    );
  };

  // Check if a specific hour is in the past (for today only)
  const isHourPast = (hour: number, pm: boolean) => {
    if (!isToday()) return false;
    const now = new Date();
    let hour24 = hour;
    if (pm && hour !== 12) hour24 = hour + 12;
    else if (!pm && hour === 12) hour24 = 0;
    return hour24 < now.getHours();
  };

  // Check if current selection is valid (not in the past)
  const isTimeValid = () => {
    if (!isToday()) return true;
    const now = new Date();
    let hour24 = selectedHour;
    if (isPM && selectedHour !== 12) hour24 = selectedHour + 12;
    else if (!isPM && selectedHour === 12) hour24 = 0;

    if (hour24 < now.getHours()) return false;
    if (hour24 === now.getHours() && selectedMinute < now.getMinutes()) return false;
    return true;
  };

  const handleConfirm = () => {
    let hour24 = selectedHour;
    if (isPM && selectedHour !== 12) {
      hour24 = selectedHour + 12;
    } else if (!isPM && selectedHour === 12) {
      hour24 = 0;
    }
    const timeStr = `${String(hour24).padStart(2, "0")}:${String(selectedMinute).padStart(2, "0")}`;
    onChange(timeStr);
  };

  const formatDisplayTime = () => {
    return `${selectedHour}:${String(selectedMinute).padStart(2, "0")} ${isPM ? "PM" : "AM"}`;
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.2 }}
      className="absolute inset-0 bg-black z-10 overflow-y-auto"
    >
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-white">{title}</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Time Display */}
        <div className="bg-gray-900 rounded-xl p-3 mb-4 text-center">
          <p className="text-2xl font-bold text-white">{formatDisplayTime()}</p>
        </div>

        {/* Hour Selection */}
        <div className="mb-3">
          <p className="text-[10px] text-gray-400 mb-1.5">Hour</p>
          <div className="grid grid-cols-6 gap-1.5">
            {hours.map((hour) => {
              const past = isHourPast(hour, isPM);
              return (
                <button
                  key={hour}
                  onClick={() => !past && setSelectedHour(hour)}
                  disabled={past}
                  className={`py-1.5 rounded-lg text-xs font-medium transition-all ${
                    past
                      ? "bg-gray-900 text-gray-600 cursor-not-allowed"
                      : selectedHour === hour
                      ? "bg-primary text-white"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                  }`}
                >
                  {hour}
                </button>
              );
            })}
          </div>
        </div>

        {/* Minute Selection */}
        <div className="mb-3">
          <p className="text-[10px] text-gray-400 mb-1.5">Minute</p>
          <div className="grid grid-cols-6 gap-1.5">
            {minutes.map((minute) => (
              <button
                key={minute}
                onClick={() => setSelectedMinute(minute)}
                className={`py-1.5 rounded-lg text-xs font-medium transition-all ${
                  selectedMinute === minute
                    ? "bg-primary text-white"
                    : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                }`}
              >
                {String(minute).padStart(2, "0")}
              </button>
            ))}
          </div>
        </div>

        {/* AM/PM Selection */}
        <div className="mb-4">
          <p className="text-[10px] text-gray-400 mb-1.5">Period</p>
          <div className="grid grid-cols-2 gap-1.5">
            <button
              onClick={() => setIsPM(false)}
              className={`py-2 rounded-lg text-xs font-medium transition-all ${
                !isPM
                  ? "bg-primary text-white"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700"
              }`}
            >
              AM
            </button>
            <button
              onClick={() => setIsPM(true)}
              className={`py-2 rounded-lg text-xs font-medium transition-all ${
                isPM
                  ? "bg-primary text-white"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700"
              }`}
            >
              PM
            </button>
          </div>
        </div>

        {/* Validation Warning */}
        {isToday() && !isTimeValid() && (
          <div className="mb-3 p-2 bg-red-900/30 border border-red-700/50 rounded-lg">
            <p className="text-[10px] text-red-400 text-center">
              Cannot select a time in the past
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-3 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isTimeValid()}
            className="flex-1 px-3 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Custom Confirmation Popup Component
const ConfirmationPopup = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "warning",
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: "warning" | "danger" | "info";
}) => {
  const getTypeStyles = () => {
    switch (type) {
      case "danger":
        return {
          icon: "bg-red-500",
          button: "bg-red-600 hover:bg-red-700",
        };
      case "info":
        return {
          icon: "bg-blue-500",
          button: "bg-blue-600 hover:bg-blue-700",
        };
      default:
        return {
          icon: "bg-yellow-500",
          button: "bg-yellow-600 hover:bg-yellow-700",
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/70 z-[100000]"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-black border border-gray-800 rounded-2xl shadow-2xl z-[60] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div
                  className={`w-12 h-12 ${styles.icon} rounded-full flex items-center justify-center flex-shrink-0`}
                >
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{title}</h3>
                  <p className="text-sm text-gray-400 mt-1">{message}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 mt-6">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-3 text-sm font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  {cancelText}
                </button>
                <button
                  onClick={() => {
                    onConfirm();
                    onClose();
                  }}
                  className={`flex-1 px-4 py-3 text-white text-sm font-semibold rounded-lg transition-colors ${styles.button}`}
                >
                  {confirmText}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

interface MeetingDetails {
  contact_date: string;
  contact_time_start: string;
  contact_time_end: string;
  meeting_title: string;
  meeting_description: string;
}

const StageChangeModal = ({
  isOpen,
  onClose,
  currentStage,
  newStage,
  onConfirm,
  requiresMeetingDetails,
}: {
  isOpen: boolean;
  onClose: () => void;
  currentStage: number;
  newStage: number;
  onConfirm: (meetingDetails?: MeetingDetails) => void;
  requiresMeetingDetails: boolean;
}) => {
  const [meetingDetails, setMeetingDetails] = useState<MeetingDetails>({
    contact_date: "",
    contact_time_start: "",
    contact_time_end: "",
    meeting_title: "",
    meeting_description: "",
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);

  const getStageName = (stage: number) => {
    return STAGES.find((s) => s.id === stage)?.name || "Unknown";
  };

  const handleConfirm = () => {
    if (requiresMeetingDetails) {
      onConfirm(meetingDetails);
    } else {
      onConfirm();
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "Select date";
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return "Select time";
    const [h, m] = timeStr.split(":");
    const hour = parseInt(h);
    const isPM = hour >= 12;
    const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${hour12}:${m} ${isPM ? "PM" : "AM"}`;
  };

  const isFormValid =
    !requiresMeetingDetails ||
    (meetingDetails.contact_date &&
      meetingDetails.contact_time_start &&
      meetingDetails.meeting_title &&
      meetingDetails.meeting_description);

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <div className="relative h-full flex flex-col">
        <div className="p-6 overflow-y-auto flex-1">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-xs font-bold text-white">
                Confirm Stage Change
              </h3>
              <p className="text-xs text-gray-400 mt-1">
                {requiresMeetingDetails
                  ? "Please provide meeting details"
                  : "Are you sure you want to change the lead stage?"}
              </p>
            </div>
          </div>

          <div className="bg-gray-900 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 mb-1">Current Stage</p>
                <p className="text-xs font-semibold text-white">
                  {getStageName(currentStage)}
                </p>
              </div>
              <div className="text-gray-400 text-xs">→</div>
              <div>
                <p className="text-xs text-gray-400 mb-1">New Stage</p>
                <p className="text-xs font-semibold text-primary">
                  {getStageName(newStage)}
                </p>
              </div>
            </div>
          </div>

          {requiresMeetingDetails && (
            <div className="space-y-4 mb-6">
              <div>
                <label className="text-xs font-semibold text-white mb-2 block">
                  Meeting Title *
                </label>
                <input
                  type="text"
                  value={meetingDetails.meeting_title}
                  onChange={(e) =>
                    setMeetingDetails((prev) => ({
                      ...prev,
                      meeting_title: e.target.value,
                    }))
                  }
                  placeholder="e.g., Initial consultation call"
                  className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-white mb-2 block">
                  Contact Date *
                </label>
                <button
                  type="button"
                  onClick={() => setShowDatePicker(true)}
                  className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg text-left flex items-center justify-between text-white hover:border-gray-600 transition-colors"
                >
                  <span className={meetingDetails.contact_date ? "text-white" : "text-gray-500"}>
                    {formatDate(meetingDetails.contact_date)}
                  </span>
                  <Calendar className="w-4 h-4 text-gray-400" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-white mb-2 block">
                    Start Time *
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowStartTimePicker(true)}
                    className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg text-left flex items-center justify-between text-white hover:border-gray-600 transition-colors"
                  >
                    <span className={meetingDetails.contact_time_start ? "text-white" : "text-gray-500"}>
                      {formatTime(meetingDetails.contact_time_start)}
                    </span>
                    <Clock className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
                <div>
                  <label className="text-xs font-semibold text-white mb-2 block">
                    End Time
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowEndTimePicker(true)}
                    className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg text-left flex items-center justify-between text-white hover:border-gray-600 transition-colors"
                  >
                    <span className={meetingDetails.contact_time_end ? "text-white" : "text-gray-500"}>
                      {formatTime(meetingDetails.contact_time_end)}
                    </span>
                    <Clock className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-white mb-2 block">
                  Meeting Description *
                </label>
                <textarea
                  value={meetingDetails.meeting_description}
                  onChange={(e) =>
                    setMeetingDetails((prev) => ({
                      ...prev,
                      meeting_description: e.target.value,
                    }))
                  }
                  placeholder="What was discussed during the meeting?"
                  rows={3}
                  className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white resize-none"
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!isFormValid}
              className="flex-1 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirm Change
            </button>
          </div>
        </div>

        <InlineDatePicker
          isOpen={showDatePicker}
          onClose={() => setShowDatePicker(false)}
          value={meetingDetails.contact_date}
          onChange={(date: string) => {
            setMeetingDetails((prev) => ({ ...prev, contact_date: date }));
            setShowDatePicker(false);
          }}
          title="Select Contact Date"
        />

        <InlineTimePicker
          isOpen={showStartTimePicker}
          onClose={() => setShowStartTimePicker(false)}
          value={meetingDetails.contact_time_start}
          onChange={(time: string) => {
            setMeetingDetails((prev) => ({ ...prev, contact_time_start: time }));
            setShowStartTimePicker(false);
          }}
          title="Select Start Time"
          selectedDate={meetingDetails.contact_date}
        />

        <InlineTimePicker
          isOpen={showEndTimePicker}
          onClose={() => setShowEndTimePicker(false)}
          value={meetingDetails.contact_time_end}
          onChange={(time: string) => {
            setMeetingDetails((prev) => ({ ...prev, contact_time_end: time }));
            setShowEndTimePicker(false);
          }}
          title="Select End Time"
          selectedDate={meetingDetails.contact_date}
        />
      </div>
    </FloatingDrawer>
  );
};

// Next Action Change Modal
interface NextActionDetails {
  action_date: string;
  action_time?: string;
  meeting_title?: string;
  meeting_description?: string;
  meeting_time_start?: string;
  meeting_time_end?: string;
}

const NextActionChangeModal = ({
  isOpen,
  onClose,
  currentAction,
  newAction,
  onConfirm,
}: {
  isOpen: boolean;
  onClose: () => void;
  currentAction: string;
  newAction: string;
  onConfirm: (actionDetails: NextActionDetails) => void;
}) => {
  const [actionDetails, setActionDetails] = useState<NextActionDetails>({
    action_date: "",
    action_time: "",
    meeting_title: "",
    meeting_description: "",
    meeting_time_start: "",
    meeting_time_end: "",
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showActionTimePicker, setShowActionTimePicker] = useState(false);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);

  const isMeeting = newAction === "book_meeting";

  const getActionName = (action: string) => {
    return NEXT_ACTIONS.find((a) => a.id === action)?.name || action?.replace(/_/g, " ");
  };

  const handleConfirm = () => {
    onConfirm(actionDetails);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "Select date";
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return "Select time";
    const [h, m] = timeStr.split(":");
    const hour = parseInt(h);
    const isPM = hour >= 12;
    const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${hour12}:${m} ${isPM ? "PM" : "AM"}`;
  };

  const isFormValid = isMeeting
    ? actionDetails.action_date &&
      actionDetails.meeting_title &&
      actionDetails.meeting_description &&
      actionDetails.meeting_time_start
    : actionDetails.action_date;

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <div className="relative h-full flex flex-col">
        <div className="p-6 overflow-y-auto flex-1">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              {newAction === "call" ? (
                <PhoneCall className="w-5 h-5 text-white" />
              ) : newAction === "email" ? (
                <Send className="w-5 h-5 text-white" />
              ) : newAction === "send_brochure" ? (
                <FileText className="w-5 h-5 text-white" />
              ) : (
                <CalendarCheck className="w-5 h-5 text-white" />
              )}
            </div>
            <div>
              <h3 className="text-xs font-bold text-white">
                Set Next Action
              </h3>
              <p className="text-xs text-gray-400 mt-1">
                {isMeeting
                  ? "Please provide meeting details"
                  : "Please set when this action should be done"}
              </p>
            </div>
          </div>

          <div className="bg-gray-900 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 mb-1">Current Action</p>
                <p className="text-xs font-semibold text-white capitalize">
                  {currentAction ? getActionName(currentAction) : "None"}
                </p>
              </div>
              <div className="text-gray-400 text-xs">→</div>
              <div>
                <p className="text-xs text-gray-400 mb-1">New Action</p>
                <p className="text-xs font-semibold text-primary capitalize">
                  {getActionName(newAction)}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            {isMeeting ? (
              <>
                <div>
                  <label className="text-xs font-semibold text-white mb-2 block">
                    Meeting Title *
                  </label>
                  <input
                    type="text"
                    value={actionDetails.meeting_title}
                    onChange={(e) =>
                      setActionDetails((prev) => ({
                        ...prev,
                        meeting_title: e.target.value,
                      }))
                    }
                    placeholder="e.g., Product Demo Meeting"
                    className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-white mb-2 block">
                    Meeting Date *
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowDatePicker(true)}
                    className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg text-left flex items-center justify-between text-white hover:border-gray-600 transition-colors"
                  >
                    <span className={actionDetails.action_date ? "text-white" : "text-gray-500"}>
                      {formatDate(actionDetails.action_date)}
                    </span>
                    <Calendar className="w-4 h-4 text-gray-400" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-white mb-2 block">
                      Start Time *
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowStartTimePicker(true)}
                      className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg text-left flex items-center justify-between text-white hover:border-gray-600 transition-colors"
                    >
                      <span className={actionDetails.meeting_time_start ? "text-white" : "text-gray-500"}>
                        {formatTime(actionDetails.meeting_time_start || "")}
                      </span>
                      <Clock className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-white mb-2 block">
                      End Time
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowEndTimePicker(true)}
                      className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg text-left flex items-center justify-between text-white hover:border-gray-600 transition-colors"
                    >
                      <span className={actionDetails.meeting_time_end ? "text-white" : "text-gray-500"}>
                        {formatTime(actionDetails.meeting_time_end || "")}
                      </span>
                      <Clock className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-white mb-2 block">
                    Meeting Description *
                  </label>
                  <textarea
                    value={actionDetails.meeting_description}
                    onChange={(e) =>
                      setActionDetails((prev) => ({
                        ...prev,
                        meeting_description: e.target.value,
                      }))
                    }
                    placeholder="What will be discussed in this meeting?"
                    rows={3}
                    className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white resize-none"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="text-xs font-semibold text-white mb-2 block">
                    Action Date *
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowDatePicker(true)}
                    className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg text-left flex items-center justify-between text-white hover:border-gray-600 transition-colors"
                  >
                    <span className={actionDetails.action_date ? "text-white" : "text-gray-500"}>
                      {formatDate(actionDetails.action_date)}
                    </span>
                    <Calendar className="w-4 h-4 text-gray-400" />
                  </button>
                </div>

                <div>
                  <label className="text-xs font-semibold text-white mb-2 block">
                    Preferred Time (Optional)
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowActionTimePicker(true)}
                    className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg text-left flex items-center justify-between text-white hover:border-gray-600 transition-colors"
                  >
                    <span className={actionDetails.action_time ? "text-white" : "text-gray-500"}>
                      {formatTime(actionDetails.action_time || "")}
                    </span>
                    <Clock className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!isFormValid}
              className="flex-1 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirm Action
            </button>
          </div>
        </div>

        <InlineDatePicker
          isOpen={showDatePicker}
          onClose={() => setShowDatePicker(false)}
          value={actionDetails.action_date}
          onChange={(date: string) => {
            setActionDetails((prev) => ({ ...prev, action_date: date }));
            setShowDatePicker(false);
          }}
          title={isMeeting ? "Select Meeting Date" : "Select Action Date"}
        />

        <InlineTimePicker
          isOpen={showActionTimePicker}
          onClose={() => setShowActionTimePicker(false)}
          value={actionDetails.action_time || ""}
          onChange={(time: string) => {
            setActionDetails((prev) => ({ ...prev, action_time: time }));
            setShowActionTimePicker(false);
          }}
          title="Select Preferred Time"
          selectedDate={actionDetails.action_date}
        />

        <InlineTimePicker
          isOpen={showStartTimePicker}
          onClose={() => setShowStartTimePicker(false)}
          value={actionDetails.meeting_time_start || ""}
          onChange={(time: string) => {
            setActionDetails((prev) => ({ ...prev, meeting_time_start: time }));
            setShowStartTimePicker(false);
          }}
          title="Select Start Time"
          selectedDate={actionDetails.action_date}
        />

        <InlineTimePicker
          isOpen={showEndTimePicker}
          onClose={() => setShowEndTimePicker(false)}
          value={actionDetails.meeting_time_end || ""}
          onChange={(time: string) => {
            setActionDetails((prev) => ({ ...prev, meeting_time_end: time }));
            setShowEndTimePicker(false);
          }}
          title="Select End Time"
          selectedDate={actionDetails.action_date}
        />
      </div>
    </FloatingDrawer>
  );
};

const AddLeadForm = ({
  isOpen,
  onClose,
  onSuccess,
  editLead,
  isEditing,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editLead?: Lead | null;
  isEditing?: boolean;
}) => {
  const [formData, setFormData] = useState({
    company_name: "",
    contact_person: "",
    email: "",
    phone: "",
    source: "website",
    priority: "medium",
    budget_range: "50000-100000",
    type: "web_development",
    industry: "technology",
    location: "",
    notes: "",
    decision_makers: "",
    lead_source: "website",
    affiliate_potential: "none",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setCustomBudgetMode(false);
    if (isEditing && editLead) {
      setFormData({
        company_name: editLead.company_name || "",
        contact_person: editLead.contact_person || "",
        email: editLead.email || "",
        phone: editLead.phone || "",
        source: editLead.source || "website",
        priority: editLead.priority || "medium",
        budget_range: editLead.budget_range || "50000-100000",
        type: editLead.type || "web_development",
        industry: editLead.industry || "technology",
        location: editLead.location || "",
        notes: editLead.notes || "",
        decision_makers: editLead.decision_makers || "",
        lead_source: editLead.lead_source || "website",
        affiliate_potential: editLead.affiliate_potential || "none",
      });
    } else if (!isEditing) {
      setFormData({
        company_name: "",
        contact_person: "",
        email: "",
        phone: "",
        source: "website",
        priority: "medium",
        budget_range: "50000-100000",
        type: "web_development",
        industry: "technology",
        location: "",
        notes: "",
        decision_makers: "",
        lead_source: "website",
        affiliate_potential: "none",
      });
    }
  }, [isEditing, editLead, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (isEditing && editLead) {
        const response = await put(`/leads/${editLead.id}`, formData);
        if (response && typeof response === 'object' && 'success' in response && response.success) {
          onSuccess();
          onClose();
        }
      } else {
        const response = await post("/leads", formData);
        if (response && typeof response === 'object' && 'success' in response && response.success) {
          onSuccess();
          onClose();
        }
      }
    } catch (error) {
      console.error("Error saving lead:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const [customBudgetMode, setCustomBudgetMode] = useState(false);
  // A saved value that isn't one of the presets (e.g. from editing) is treated as custom.
  const budgetIsCustom = customBudgetMode || (!!formData.budget_range && !BUDGET_RANGES.includes(formData.budget_range));

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">
            {isEditing ? "Edit Lead" : "Add New Lead"}
          </h2>
          <p className="text-xs text-gray-400 mt-1">
            {isEditing ? "Update lead information" : "Create a new sales lead"}
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex-1 overflow-y-auto p-6 space-y-4"
      >
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">
            Company Name *
          </label>
          <input
            type="text"
            required
            value={formData.company_name}
            onChange={(e) => handleChange("company_name", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">
            Contact Person *
          </label>
          <input
            type="text"
            required
            value={formData.contact_person}
            onChange={(e) => handleChange("contact_person", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              Email *
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              Phone (Optional)
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              placeholder="Optional"
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              Project Type *
            </label>
            <select
              value={formData.type}
              onChange={(e) => handleChange("type", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            >
              {PROJECT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              Industry *
            </label>
            <select
              value={formData.industry}
              onChange={(e) => handleChange("industry", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            >
              {INDUSTRIES.map((industry) => (
                <option key={industry} value={industry}>
                  {industry
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              Lead Source
            </label>
            <select
              value={formData.lead_source}
              onChange={(e) => handleChange("lead_source", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            >
              {LEAD_SOURCES.map((source) => (
                <option key={source} value={source}>
                  {source
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              Priority
            </label>
            <select
              value={formData.priority}
              onChange={(e) => handleChange("priority", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            >
              {PRIORITY_LEVELS.map((priority) => (
                <option key={priority} value={priority}>
                  {priority.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">
            Affiliate Potential
          </label>
          <select
            value={formData.affiliate_potential}
            onChange={(e) => handleChange("affiliate_potential", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          >
            {AFFILIATE_POTENTIALS.map((potential) => (
              <option key={potential} value={potential}>
                {potential
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase())}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">
            Budget Range
          </label>
          <select
            value={budgetIsCustom ? CUSTOM_BUDGET : formData.budget_range}
            onChange={(e) => {
              if (e.target.value === CUSTOM_BUDGET) {
                setCustomBudgetMode(true);
                handleChange("budget_range", "");
              } else {
                setCustomBudgetMode(false);
                handleChange("budget_range", e.target.value);
              }
            }}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          >
            {BUDGET_RANGES.map((range) => (
              <option key={range} value={range}>
                ${range}
              </option>
            ))}
            <option value={CUSTOM_BUDGET}>Custom amount / range…</option>
          </select>
          {budgetIsCustom && (
            <input
              type="text"
              value={formData.budget_range}
              onChange={(e) => handleChange("budget_range", e.target.value)}
              placeholder="e.g. 250 or 250-400"
              autoFocus
              className="mt-2 w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          )}
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">
            Location
          </label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => handleChange("location", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">
            Notes
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white resize-none"
          />
        </div>
      </form>

      <div className="p-6 border-t border-gray-800 space-y-3">
        <button
          onClick={handleSubmit}
          disabled={
            isSubmitting ||
            !formData.company_name ||
            !formData.contact_person ||
            !formData.email
          }
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              {isEditing ? "Updating..." : "Creating..."}
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              {isEditing ? "Update Lead" : "Create Lead"}
            </>
          )}
        </button>

        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </FloatingDrawer>
  );
};

const SERVICE_TYPES = [
  "social_media_management",
  "content_creation",
  "seo",
  "paid_advertising",
  "app_deployment",
  "talent_placement",
  "web_development",
  "consulting",
  "other",
];

const LeadDrawer = ({
  lead,
  isOpen,
  onClose,
  onStageChange,
  onNextActionChange,
  onConvert,
  onConvertToClient,
  onDelete,
  onEdit,
}: {
  lead: Lead | null;
  isOpen: boolean;
  onClose: () => void;
  onStageChange: (leadId: string, stage: number, meetingDetails?: MeetingDetails) => void;
  onNextActionChange: (leadId: string, action: string, actionDetails: NextActionDetails) => void;
  onConvert: (leadId: string, value: number) => void;
  onConvertToClient: (leadId: string, contractData: {
    service_type: string;
    start_date: string;
    duration_months: number;
    value?: number;
    billing_cycle: string;
  }) => void;
  onDelete: (leadId: string) => void;
  onEdit: (lead: Lead) => void;
}) => {
  const [currentView, setCurrentView] = useState<
    "details" | "convert" | "convertToClient" | "stageConfirm" | "actionConfirm"
  >("details");
  const [selectedStage, setSelectedStage] = useState(1);
  const [selectedAction, setSelectedAction] = useState("");
  const [projectValue, setProjectValue] = useState("");
  const [isConverting, setIsConverting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [clientForm, setClientForm] = useState({
    service_type: SERVICE_TYPES[0],
    start_date: new Date().toISOString().slice(0, 10),
    duration_months: "6",
    value: "",
    billing_cycle: "monthly",
  });
  const [isConvertingToClient, setIsConvertingToClient] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCurrentView("details");
      setProjectValue("");
      setSelectedStage(lead?.current_stage || 1);
      setSelectedAction(lead?.next_action || "");
      setClientForm({
        service_type: SERVICE_TYPES[0],
        start_date: new Date().toISOString().slice(0, 10),
        duration_months: "6",
        value: lead?.value ? String(lead.value) : "",
        billing_cycle: "monthly",
      });
    }
  }, [isOpen, lead]);

  if (!lead) return null;

  const handleStageChange = (newStage: number) => {
    setSelectedStage(newStage);
    setCurrentView("stageConfirm");
  };

  const handleNextActionChange = (newAction: string) => {
    setSelectedAction(newAction);
    setCurrentView("actionConfirm");
  };

  const confirmStageChange = (meetingDetails?: MeetingDetails) => {
    onStageChange(lead.id, selectedStage, meetingDetails);
    setCurrentView("details");
  };

  const confirmNextActionChange = (actionDetails: NextActionDetails) => {
    onNextActionChange(lead.id, selectedAction, actionDetails);
    setCurrentView("details");
  };

  const handleConvert = async () => {
    if (!projectValue || isNaN(parseFloat(projectValue))) {
      alert("Please enter a valid project value");
      return;
    }

    setIsConverting(true);
    try {
      onConvert(lead.id, parseFloat(projectValue));
      onClose();
    } catch (error) {
      console.error("Error converting lead:", error);
    } finally {
      setIsConverting(false);
    }
  };

  const handleConvertToClient = async () => {
    if (!clientForm.start_date || !clientForm.duration_months) {
      alert("Please fill in the contract start date and duration");
      return;
    }

    setIsConvertingToClient(true);
    try {
      onConvertToClient(lead.id, {
        service_type: clientForm.service_type,
        start_date: clientForm.start_date,
        duration_months: parseInt(clientForm.duration_months, 10),
        value: clientForm.value ? parseFloat(clientForm.value) : undefined,
        billing_cycle: clientForm.billing_cycle,
      });
      onClose();
    } catch (error) {
      console.error("Error converting lead to client:", error);
    } finally {
      setIsConvertingToClient(false);
    }
  };

  const getStageInfo = (stage: number) => {
    return STAGES.find((s) => s.id === stage) || STAGES[0];
  };

  const isContactedStage = selectedStage === 2 && lead.current_stage !== 2;

  const renderDetailsView = () => (
    <>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Lead Details</h2>
          <p className="text-xs text-gray-400 mt-1">{lead.company_name}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              Current Stage
            </label>
            <select
              value={lead.current_stage}
              onChange={(e) => handleStageChange(parseInt(e.target.value))}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            >
              {STAGES.map((stage) => (
                <option key={stage.id} value={stage.id}>
                  {stage.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              Status
            </label>
            <div
              className={`px-3 py-2 text-xs font-semibold rounded-lg ${
                getStageInfo(lead.current_stage).color
              }`}
            >
              {getStageInfo(lead.current_stage).name}
            </div>
          </div>
        </div>

        {/* Next Action Dropdown - Like Current Stage */}
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">
            Next Action
          </label>
          <select
            value={lead.next_action || ""}
            onChange={(e) => handleNextActionChange(e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          >
            <option value="">Select Action</option>
            {NEXT_ACTIONS.map((action) => (
              <option key={action.id} value={action.id}>
                {action.name}
              </option>
            ))}
          </select>
        </div>

        {/* Next Step Display */}
        {lead.next_action && lead.next_action_date && (
          <div className="bg-gradient-to-r from-primary/40 to-blue-primary/20 border border-primary/50 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                {lead.next_action === "call" ? (
                  <PhoneCall className="w-3 h-3 text-white" />
                ) : lead.next_action === "email" ? (
                  <Send className="w-3 h-3 text-white" />
                ) : lead.next_action === "send_brochure" ? (
                  <FileText className="w-3 h-3 text-white" />
                ) : (
                  <CalendarCheck className="w-3 h-3 text-white" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold text-primary uppercase tracking-wide mb-1">
                  Next Step
                </p>
                <p className="text-sm font-semibold text-white">
                  {lead.next_action === "call" && "Call Client"}
                  {lead.next_action === "email" && "Send Email"}
                  {lead.next_action === "send_brochure" && "Send Brochure"}
                  {lead.next_action === "book_meeting" && (lead.meeting_title || "Meeting Scheduled")}
                </p>
                <p className="text-xs text-gray-300 mt-1">
                  <span className="text-gray-400">Scheduled for:</span>{" "}
                  {new Date(lead.next_action_date).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                  {lead.next_action === "book_meeting" && lead.meeting_time_start && (
                    <span>
                      {" "}at {(() => {
                        const [h, m] = (lead.meeting_time_start || "").split(":");
                        const hour = parseInt(h);
                        const isPM = hour >= 12;
                        const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
                        return `${hour12}:${m} ${isPM ? "PM" : "AM"}`;
                      })()}
                    </span>
                  )}
                  {lead.next_action !== "book_meeting" && lead.next_action_time && (
                    <span>
                      {" "}at {(() => {
                        const [h, m] = (lead.next_action_time || "").split(":");
                        const hour = parseInt(h);
                        const isPM = hour >= 12;
                        const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
                        return `${hour12}:${m} ${isPM ? "PM" : "AM"}`;
                      })()}
                    </span>
                  )}
                </p>
                {lead.next_action === "book_meeting" && lead.meeting_description && (
                  <p className="text-xs text-gray-400 mt-2 italic">
                    "{lead.meeting_description}"
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Building className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Company</p>
              <p className="text-xs text-gray-400">{lead.company_name}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Target className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Project Type</p>
              <p className="text-xs text-gray-400 capitalize">
                {lead.type?.replace(/_/g, " ")}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Building className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Industry</p>
              <p className="text-xs text-gray-400 capitalize">
                {lead.industry?.replace(/_/g, " ")}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <User className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Contact Person</p>
              <p className="text-xs text-gray-400">{lead.contact_person}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Email</p>
              <p className="text-xs text-gray-400">{lead.email}</p>
            </div>
          </div>

          {lead.phone && (
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">Phone</p>
                <p className="text-xs text-gray-400">{lead.phone}</p>
              </div>
            </div>
          )}

          {lead.location && (
            <div className="flex items-center gap-3">
              <MapPin className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">Location</p>
                <p className="text-xs text-gray-400">{lead.location}</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Target className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Source</p>
              <p className="text-xs text-gray-400 capitalize">
                {lead.source?.replace(/_/g, " ")}
              </p>
            </div>
          </div>

          {lead.lead_source && (
            <div className="flex items-center gap-3">
              <Target className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">Lead Source</p>
                <p className="text-xs text-gray-400 capitalize">
                  {lead.lead_source?.replace(/_/g, " ")}
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Star className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Priority</p>
              <p className={`text-xs capitalize ${
                lead.priority === "high"
                  ? "text-red-400"
                  : lead.priority === "medium"
                  ? "text-yellow-400"
                  : "text-green-400"
              }`}>
                {lead.priority || "Not set"}
              </p>
            </div>
          </div>

          {lead.affiliate_potential && (
            <div className="flex items-center gap-3">
              <Users className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">Affiliate Potential</p>
                <p className="text-xs text-gray-400 capitalize">
                  {lead.affiliate_potential?.replace(/_/g, " ")}
                </p>
              </div>
            </div>
          )}

          {lead.budget_range && (
            <div className="flex items-center gap-3">
              <DollarSign className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">Budget Range</p>
                <p className="text-xs text-gray-400">${lead.budget_range}</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Created</p>
              <p className="text-xs text-gray-400">
                {new Date(lead.created_at).toLocaleDateString()} by{" "}
                {lead.first_name} {lead.last_name}
              </p>
            </div>
          </div>

          {lead.last_contacted_at && (
            <div className="flex items-center gap-3">
              <Clock className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">
                  Last Contacted
                </p>
                <p className="text-xs text-gray-400">
                  {new Date(lead.last_contacted_at).toLocaleString()}
                  {lead.contacted_by_first_name &&
                    lead.contacted_by_last_name && (
                      <span>
                        {" "}
                        by {lead.contacted_by_first_name}{" "}
                        {lead.contacted_by_last_name}
                      </span>
                    )}
                </p>
              </div>
            </div>
          )}
        </div>

        {lead.notes && (
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              Notes
            </label>
            <p className="text-xs text-gray-400 bg-gray-900 p-3 rounded-lg">
              {lead.notes}
            </p>
          </div>
        )}
      </div>

      <div className="p-3 border-t border-gray-800 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => onEdit(lead)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Edit3 className="w-4 h-4" />
            Edit Lead
          </button>

          <button
            onClick={() => setCurrentView("convert")}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs rounded-lg hover:bg-primary/90 transition-colors"
          >
            <FaProjectDiagram className="w-4 h-4" />
            Convert to Project
          </button>
        </div>

        <button
          onClick={() => setCurrentView("convertToClient")}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors"
        >
          <Building2 className="w-4 h-4" />
          Convert to Client (Retainer)
        </button>

        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          Delete Lead
        </button>
      </div>

      <ConfirmationPopup
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => onDelete(lead.id)}
        title="Delete Lead"
        message="Are you sure you want to delete this lead? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </>
  );

  const renderConvertView = () => (
    <>
      <div className="flex items-center gap-3 p-6 border-b border-gray-800">
        <button
          onClick={() => setCurrentView("details")}
          className="p-2 rounded-lg text-gray-400 hover:bg-gray-800 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <div>
          <h2 className="text-xs font-bold text-white">Convert to Project</h2>
          <p className="text-xs text-gray-400 mt-1">{lead.company_name}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              Project Value ({lead.currency})
            </label>
            <input
              type="number"
              value={projectValue}
              onChange={(e) => setProjectValue(e.target.value)}
              placeholder="Enter project value"
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white placeholder:text-gray-500"
            />
          </div>

          <div className="bg-gray-900 rounded-lg p-4">
            <p className="text-xs text-gray-400 mb-2">Lead Information</p>
            <p className="text-xs text-white">{lead.company_name}</p>
            <p className="text-xs text-gray-400 mt-1">{lead.contact_person}</p>
          </div>
        </div>
      </div>

      <div className="p-6 border-t border-gray-800 space-y-3">
        <button
          onClick={() => setCurrentView("details")}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleConvert}
          disabled={!projectValue || isConverting}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isConverting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Converting...
            </>
          ) : (
            <>
              <FaProjectDiagram className="w-4 h-4" />
              Convert to Project
            </>
          )}
        </button>
      </div>
    </>
  );

  const renderConvertToClientView = () => (
    <>
      <div className="flex items-center gap-3 p-6 border-b border-gray-800">
        <button
          onClick={() => setCurrentView("details")}
          className="p-2 rounded-lg text-gray-400 hover:bg-gray-800 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <div>
          <h2 className="text-xs font-bold text-white">Convert to Client</h2>
          <p className="text-xs text-gray-400 mt-1">{lead.company_name}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <div className="bg-gray-900 rounded-lg p-4 mb-2">
          <p className="text-xs text-gray-400 mb-2">This lead will become a client, and this contract will be its first.</p>
          <p className="text-xs text-white">{lead.company_name}</p>
          <p className="text-xs text-gray-400 mt-1">{lead.contact_person}</p>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Service Type *</label>
          <select
            value={clientForm.service_type}
            onChange={(e) => setClientForm((prev) => ({ ...prev, service_type: e.target.value }))}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          >
            {SERVICE_TYPES.map((type) => (
              <option key={type} value={type}>
                {type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Start Date *</label>
            <DatePicker dark value={clientForm.start_date} onChange={(v) => setClientForm((prev) => ({ ...prev, start_date: v }))} required />
          </div>
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Duration (months) *</label>
            <input
              type="number"
              min={1}
              value={clientForm.duration_months}
              onChange={(e) => setClientForm((prev) => ({ ...prev, duration_months: e.target.value }))}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Contract Value ({lead.currency})</label>
            <input
              type="number"
              value={clientForm.value}
              onChange={(e) => setClientForm((prev) => ({ ...prev, value: e.target.value }))}
              placeholder="Per billing cycle"
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white placeholder:text-gray-500"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Billing Cycle</label>
            <select
              value={clientForm.billing_cycle}
              onChange={(e) => setClientForm((prev) => ({ ...prev, billing_cycle: e.target.value }))}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="one_time">One-time</option>
            </select>
          </div>
        </div>
      </div>

      <div className="p-6 border-t border-gray-800 space-y-3">
        <button
          onClick={() => setCurrentView("details")}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleConvertToClient}
          disabled={!clientForm.start_date || !clientForm.duration_months || isConvertingToClient}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isConvertingToClient ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Converting...
            </>
          ) : (
            <>
              <Building2 className="w-4 h-4" />
              Convert to Client
            </>
          )}
        </button>
      </div>
    </>
  );

  return (
    <>
      <FloatingDrawer
        isOpen={isOpen && currentView !== "stageConfirm" && currentView !== "actionConfirm"}
        onClose={onClose}
      >
        {currentView === "details" && renderDetailsView()}
        {currentView === "convert" && renderConvertView()}
        {currentView === "convertToClient" && renderConvertToClientView()}
      </FloatingDrawer>

      <StageChangeModal
        isOpen={currentView === "stageConfirm"}
        onClose={() => setCurrentView("details")}
        currentStage={lead.current_stage}
        newStage={selectedStage}
        onConfirm={confirmStageChange}
        requiresMeetingDetails={isContactedStage}
      />

      <NextActionChangeModal
        isOpen={currentView === "actionConfirm"}
        onClose={() => setCurrentView("details")}
        currentAction={lead.next_action || ""}
        newAction={selectedAction}
        onConfirm={confirmNextActionChange}
      />
    </>
  );
};

function ActionsCell({
  onView,
  onDelete,
}: {
  onView: () => void;
  onDelete: () => void;
}) {
  const { isOpen, position, handleOpen, handleClose } = useTableDropdown();

  return (
    <TableActions>
      <TableDropdown>
        <TableDropdownButton onClick={handleOpen} />
        <TableDropdownMenu
          isOpen={isOpen}
          onClose={handleClose}
          position={position}
        >
          <TableDropdownItem
            onClick={() => {
              onView();
              handleClose();
            }}
          >
            <Eye className="w-3 h-3" />
            View Details
          </TableDropdownItem>
          <TableDropdownItem
            onClick={() => {
              onDelete();
              handleClose();
            }}
            className="text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-3 h-3" />
            Delete
          </TableDropdownItem>
        </TableDropdownMenu>
      </TableDropdown>
    </TableActions>
  );
}

function LeadsPage() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery] = useState("");
  const [statusFilter] = useState("all");
  const [stageFilter] = useState("all");
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showLeadDrawer, setShowLeadDrawer] = useState(false);
  const [showAddLead, setShowAddLead] = useState(false);
  const [isEditingLead, setIsEditingLead] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    per_page: 5,
    total: 0,
    total_pages: 0,
  });

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const response = await get<LeadsResponse>(
        `/leads?page=${pagination.page}&page_size=${pagination.per_page}`
      );
      if (response.success) {
        setLeads(response.data);
        setPagination((prev) => ({
          ...prev,
          total: response.pagination.total,
          total_pages: response.pagination.total_pages,
        }));
      }
    } catch (error) {
      console.error("Error fetching leads:", error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleStageChange = async (
    leadId: string,
    stage: number,
    meetingDetails?: MeetingDetails
  ) => {
    try {
      const updateData: Record<string, string | number | undefined> = {
        status:
          STAGES.find((s) => s.id === stage)
            ?.name.toLowerCase()
            .replace(" ", "_") || "new",
      };

      if (meetingDetails) {
        updateData.contact_date = meetingDetails.contact_date;
        updateData.contact_time_start = meetingDetails.contact_time_start;
        updateData.contact_time_end = meetingDetails.contact_time_end;
        updateData.meeting_title = meetingDetails.meeting_title;
        updateData.meeting_description = meetingDetails.meeting_description;
      }

      await put(`/leads/${leadId}`, updateData);
      await put(`/leads/${leadId}/stage`, { stage });

      setLeads((prev) =>
        prev.map((lead) =>
          lead.id === leadId ? { ...lead, current_stage: stage } : lead
        )
      );
      if (selectedLead?.id === leadId) {
        setSelectedLead((prev) =>
          prev ? { ...prev, current_stage: stage } : null
        );
      }

      fetchLeads();
    } catch (error) {
      console.error("Error updating stage:", error);
    }
  };

  const handleNextActionChange = async (
    leadId: string,
    action: string,
    actionDetails: NextActionDetails
  ) => {
    try {
      const updateData: Record<string, string | undefined> = {
        next_action: action,
        next_action_date: actionDetails.action_date,
      };

      if (action === "book_meeting") {
        updateData.meeting_title = actionDetails.meeting_title;
        updateData.meeting_description = actionDetails.meeting_description;
        updateData.meeting_time_start = actionDetails.meeting_time_start;
        updateData.meeting_time_end = actionDetails.meeting_time_end;
      }

      await put(`/leads/${leadId}`, updateData);

      setLeads((prev) =>
        prev.map((lead) =>
          lead.id === leadId
            ? { ...lead, next_action: action, next_action_date: actionDetails.action_date }
            : lead
        )
      );
      if (selectedLead?.id === leadId) {
        setSelectedLead((prev) =>
          prev ? { ...prev, next_action: action, next_action_date: actionDetails.action_date } : null
        );
      }

      fetchLeads();
    } catch (error) {
      console.error("Error updating next action:", error);
    }
  };

  const handleConvert = async (leadId: string, value: number) => {
    try {
      const response = await put(`/leads/${leadId}/convert`, { value });
      if (response && typeof response === 'object' && 'success' in response && response.success) {
        alert("Lead successfully converted to project");
        setLeads((prev) => prev.filter((lead) => lead.id !== leadId));
        fetchLeads();
      }
    } catch (error) {
      console.error("Error converting lead:", error);
      alert("Error converting lead to project");
    }
  };

  const handleConvertToClient = async (leadId: string, contractData: {
    service_type: string;
    start_date: string;
    duration_months: number;
    value?: number;
    billing_cycle: string;
  }) => {
    try {
      const response = await put(`/leads/${leadId}/convert-to-client`, contractData);
      if (response && typeof response === 'object' && 'success' in response && response.success) {
        alert("Lead successfully converted to client");
        setLeads((prev) => prev.filter((lead) => lead.id !== leadId));
        fetchLeads();
      }
    } catch (error) {
      console.error("Error converting lead to client:", error);
      alert("Error converting lead to client");
    }
  };

  const handleDeleteClick = (leadId: string) => {
    setLeadToDelete(leadId);
    setShowDeleteConfirm(true);
  };

  const handleBatchDelete = async (rows: { id: string }[]) => {
    if (!confirm(`Delete ${rows.length} selected leads? This cannot be undone.`)) return;

    const results = await Promise.allSettled(rows.map((r) => del(`/leads/${r.id}`)));
    const deleted = new Set(rows.filter((_, i) => results[i].status === "fulfilled").map((r) => r.id));
    setLeads((prev: any[]) => prev.filter((item) => !deleted.has(item.id)));
    const failed = rows.length - deleted.size;
    alert(failed ? `${deleted.size} deleted, ${failed} failed` : `${deleted.size} leads deleted successfully`);
  };

  const handleDelete = async (leadId: string) => {
    try {
      await del(`/leads/${leadId}`);
      setLeads((prev) => prev.filter((lead) => lead.id !== leadId));
      if (selectedLead?.id === leadId) {
        setShowLeadDrawer(false);
        setSelectedLead(null);
      }
    } catch (error) {
      console.error("Error deleting lead:", error);
    }
  };

  const confirmDelete = async () => {
    if (leadToDelete) {
      await handleDelete(leadToDelete);
      setLeadToDelete(null);
    }
  };

  const handleEditLead = (lead: Lead) => {
    setEditingLead(lead);
    setIsEditingLead(true);
    setShowLeadDrawer(false);
    setShowAddLead(true);
  };

  const filteredLeads = leads.filter((lead) => {
    const matchesSearch =
      lead.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.contact_person.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || lead.status === statusFilter;
    const matchesStage =
      stageFilter === "all" || lead.current_stage.toString() === stageFilter;

    return matchesSearch && matchesStatus && matchesStage;
  });

  const getStageInfo = (stage: number) => {
    return STAGES.find((s) => s.id === stage) || STAGES[0];
  };

  const handleViewLead = (lead: Lead) => {
    setSelectedLead(lead);
    setShowLeadDrawer(true);
  };

  const handleAddLeadSuccess = () => {
    fetchLeads();
    setIsEditingLead(false);
    setEditingLead(null);
  };

  const handleCloseAddLead = () => {
    setShowAddLead(false);
    setIsEditingLead(false);
    setEditingLead(null);
  };

  const columns: ColumnDef<Lead>[] = [
    {
      accessorKey: "company_name",
      header: "Company",
      cell: ({ row }) => (
        <div>
          <p className="text-xs font-semibold text-gray-900">
            {row.original.company_name}
          </p>
          <p className="text-xs text-gray-500 capitalize">
            {row.original.industry?.replace(/_/g, " ")}
          </p>
        </div>
      ),
    },
    {
      accessorKey: "email",
      header: "Contact Person",
      cell: ({ row }) => (
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-900">{row.original.contact_person} </p>
          <p className="text-xs text-gray-600">{row.original.email}</p>
          <p className="text-xs text-gray-600">{row.original.phone}</p>
        </div>
      ),
    },
    {
      accessorKey: "current_stage",
      header: "Stage",
      cell: ({ row }) => {
        const stage = getStageInfo(row.original.current_stage);
        return (
          <span
            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${stage.color}`}
          >
            {stage.name}
          </span>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }) => (
        <div>
          <p className="text-xs text-gray-600">
            {new Date(row.original.created_at).toLocaleDateString()}
          </p>
          <p className="text-xs text-gray-500">
            by {row.original.first_name} {row.original.last_name}
          </p>
        </div>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <ActionsCell
          onView={() => handleViewLead(row.original)}
          onDelete={() => handleDeleteClick(row.original.id)}
        />
      ),
    },
  ];

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full">
        <PageHeader title="Lead Management" description="Streamline your sales pipeline and track lead progression">
          <button
              onClick={() => setShowAddLead(true)}
              className="px-5 py-1.5 bg-gradient-to-r from-primary to-primary/90 text-white text-sm font-light tracking-wide rounded-full hover:from-black/90 hover:to-black transition-all duration-200  min-w-[140px]"
            >
              Add New Lead
            </button>
        </PageHeader>

        {/* Table Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <SkeletonRow />
          ) : (
            <>
              <TableProvider columns={columns} data={filteredLeads} onDeleteSelected={handleBatchDelete} onAddRow={() => setShowAddLead(true)} addRowLabel="Add lead">
                <DataTableHeader>
                  {({ headerGroup }) => (
                    <DataTableHeaderGroup headerGroup={headerGroup}>
                      {({ header }) => <DataTableHead header={header} />}
                    </DataTableHeaderGroup>
                  )}
                </DataTableHeader>
                <DataTableBody>
                  {({ row }) => (
                    <DataTableRow
                      row={row}
                      className="hover:bg-gray-50 transition-colors border-b border-gray-100"
                    >
                      {({ cell }) => <DataTableCell cell={cell} />}
                    </DataTableRow>
                  )}
                </DataTableBody>
              </TableProvider>

              {filteredLeads.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Target className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-light text-gray-900 mb-2">
                    No leads found
                  </h3>
                  <p className="text-sm font-light text-gray-500 mb-6 max-w-md mx-auto">
                    {searchQuery ||
                    statusFilter !== "all" ||
                    stageFilter !== "all"
                      ? "Try adjusting your search criteria or filters to find what you're looking for."
                      : "Start building your sales pipeline by adding your first lead."}
                  </p>
                  {!searchQuery &&
                    statusFilter === "all" &&
                    stageFilter === "all" && (
                      <button
                        onClick={() => setShowAddLead(true)}
                        className="px-6 py-2 bg-primary text-white text-sm font-light rounded-lg hover:bg-primary/90 transition-all duration-200"
                      >
                        Create First Lead
                      </button>
                    )}
                </div>
              )}

              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <p className="text-xs text-gray-600">
                    Showing{" "}
                    {((pagination.page - 1) * pagination.per_page) + 1}–
                    {Math.min(pagination.page * pagination.per_page, pagination.total)}{" "}
                    of {pagination.total} leads
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      aria-label="Previous page"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="p-1.5 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                      <ChevronLeft className="w-3 h-3" />
                    </button>
                    <span className="px-2 py-1 text-xs text-gray-600">
                      Page {pagination.page} of {pagination.total_pages}
                    </span>
                    <button
                      type="button"
                      aria-label="Next page"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.total_pages}
                      className="p-1.5 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <LeadDrawer
        lead={selectedLead}
        isOpen={showLeadDrawer}
        onClose={() => {
          setShowLeadDrawer(false);
          setSelectedLead(null);
        }}
        onStageChange={handleStageChange}
        onNextActionChange={handleNextActionChange}
        onConvert={handleConvert}
        onConvertToClient={handleConvertToClient}
        onDelete={handleDelete}
        onEdit={handleEditLead}
      />

      <AddLeadForm
        isOpen={showAddLead}
        onClose={handleCloseAddLead}
        onSuccess={handleAddLeadSuccess}
        editLead={editingLead}
        isEditing={isEditingLead}
      />

      <ConfirmationPopup
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setLeadToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Lead"
        message="Are you sure you want to delete this lead? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
}

export default LeadsPage;
